import { defineStore } from 'pinia'

import { IS_DEV_ENV } from '@/core/constants'
import companyServices from '@/features/companies/services/companyServices'
import type {
  Company,
  CompanyStore,
  CompanyAddress,
  CompanyContact,
  CompanyInfo,
  CompanyData,
} from '@/features/companies/types'
import type { Response as Res } from '@/shared/types/response'

const KEY_CURRENT_COMPANY = 'RB Empresas - Empresa Atual'

const useCompanyStore = defineStore('company', {
  state: (): CompanyStore => ({
    isFetching: false,
    companies: [],
    currentCompany: null,
    companyData: null,
  }),

  actions: {
    async fetchCompanies(userId: string): Promise<Res<Company[]>> {
      this.isFetching = true
      const getUserId = IS_DEV_ENV ? import.meta.env.VITE_DEV_USER_ID : userId
      const response = await companyServices.fetchCompanies(getUserId)
      this.isFetching = false

      if (response.ok) {
        this.companies = response.data
      }

      return response
    },

    async fetchCompanyData(companyId: string): Promise<Res<CompanyData>> {
      this.isFetching = true
      const response = await companyServices.fetchCompanyData(companyId)
      this.isFetching = false

      if (response.ok) {
        this.companyData = response.data
      }

      return response
    },

    async updateCompanyInfo(payload: CompanyInfo): Promise<Res<CompanyInfo>> {
      const companyId = this.getCurrentCompany('id')
      const response = await companyServices.updateInfo(companyId, payload)

      if (response.ok && this.companyData) {
        this.companyData = { ...this.companyData, ...response.data }
      }

      return response
    },

    async updateCompanyContact(payload: Omit<CompanyContact, 'email'>): Promise<Res<CompanyContact>> {
      const companyId = this.getCurrentCompany('id')
      const response = await companyServices.updateContact(companyId, {
        ...payload,
        email: this.companyData ? this.companyData.email : '',
      })

      if (response.ok && this.companyData) {
        this.companyData = { ...this.companyData, ...response.data }
      }

      return response
    },

    async updateCompanyAddress(payload: CompanyAddress): Promise<Res<CompanyAddress>> {
      const companyId = this.getCurrentCompany('id')
      const response = await companyServices.updateAddress(companyId, payload)

      if (response.ok && this.companyData) {
        this.companyData = { ...this.companyData, address: response.data }
      }

      return response
    },

    getCurrentCompany(key: 'id' | 'tradeName' | 'slug'): string {
      if (!this.currentCompany) {
        this.loadCompanyFromSessionStorage()
      }

      return this.currentCompany ? this.currentCompany[key] : ''
    },

    loadCompanyFromSessionStorage(): void {
      const company = sessionStorage.getItem(KEY_CURRENT_COMPANY)
      if (company) {
        this.currentCompany = JSON.parse(company)
      }
    },

    setCurrentCompany(company: Company): void {
      this.currentCompany = company
      sessionStorage.setItem(KEY_CURRENT_COMPANY, JSON.stringify(company))
    },

    cleanCompanies(): void {
      this.companies = []
      this.currentCompany = null
      this.companyData = null
      sessionStorage.removeItem(KEY_CURRENT_COMPANY)
    },
  },
})

export default useCompanyStore
